import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownAZ, Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { useWorld } from "@/context/WorldContext";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { SHIP_STATUSES } from "@/lib/constants";
import { SHIP_SORTERS, isOtp, pairingLine, shipLabel, shipMembers, shipsOf } from "@/lib/infobox";

const initials = (t) => t.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export const useShipSort = () => {
  const { basePath } = useWorld();
  const key = `lorely.shipSort:${basePath}`;
  const [sort, setState] = useState(() => localStorage.getItem(key) || "status");
  const set = (v) => { localStorage.setItem(key, v); setState(v); };
  return [sort, set];
};

export const ShipLabelBadge = ({ label, className = "" }) =>
  label ? <span className={`inline-block text-[10px] font-code uppercase tracking-wider rounded-full border px-2 py-0.5 ${SHIP_STATUSES[label]?.cls || "text-slate-300 border-line"} ${className}`} data-testid="ship-status-badge">{label}</span> : null;

export const PairingLine = ({ page, className = "" }) => {
  const { pages } = useWorld();
  const line = pairingLine(page, pages);
  return line ? <p className={`font-code text-pink-300 ${className}`} data-testid="ship-pairing-line">{line}</p> : null;
};

export const Portrait = ({ page, size = "w-8 h-8 text-[11px]", className = "" }) =>
  page?.image ? (
    <img src={fileUrl(page.image)} alt={page.title} className={`${size} rounded-full object-cover shrink-0 ${className}`} />
  ) : (
    <div className={`${size} rounded-full bg-amber-500/10 text-amber-200 font-display flex items-center justify-center shrink-0 ${className}`}>{initials(page?.title || "?")}</div>
  );

export const ShipAvatars = ({ page, size = "w-16 h-16 text-xl" }) => {
  const { pages } = useWorld();
  return (
    <div className="flex items-center justify-center -space-x-4" data-testid="ship-avatars">
      {shipMembers(page, pages).slice(0, 5).map((m, i) => (
        <Portrait key={i} page={m.page || { title: m.item.text }} size={size} className="border-2 border-panel ring-1 ring-pink-500/40 !bg-pink-500/10 !text-pink-200" />
      ))}
    </div>
  );
};

const OtpStar = ({ ship, charId }) => {
  const { readOnly, reload } = useWorld();
  const [busy, setBusy] = useState(false);
  const on = isOtp(ship, charId);
  if (readOnly) return on ? <Star className="w-4 h-4 text-amber-300 fill-amber-300 shrink-0" data-testid={`otp-star-static-${ship.id}`} /> : null;
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/pages/${ship.id}/otp`, { character_id: charId, otp: !on });
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  return (
    <button type="button" onClick={toggle} disabled={busy} title={on ? "Remove OTP" : "Mark as OTP"} aria-pressed={on} data-testid={`otp-toggle-${ship.id}`}
      className="p-1 rounded-full hover:bg-amber-500/10 transition-colors shrink-0 disabled:opacity-50">
      <Star key={String(on)} className={`w-4 h-4 transition-colors ${on ? "star-pop text-amber-300 fill-amber-300" : "text-slate-600 hover:text-amber-300"}`} />
    </button>
  );
};

const ShipPanelItem = ({ ship, charId }) => {
  const { pages, basePath } = useWorld();
  const on = isOtp(ship, charId);
  return (
    <div className={`flex items-start gap-2 rounded-lg px-2 py-1.5 -mx-2 transition-colors ${on ? "bg-amber-500/[0.07] ring-1 ring-amber-500/20" : ""}`} data-testid={`ship-panel-item-${ship.id}`} data-otp={on}>
      <Link to={`${basePath}/p/${ship.id}`} className="block group min-w-0 flex-1" data-testid={`ship-panel-link-${ship.id}`}>
        <span className="flex items-center gap-2">
          <span className="text-sm text-slate-200 group-hover:text-pink-300 transition-colors truncate">{ship.title}</span>
          <ShipLabelBadge label={shipLabel(ship)} />
        </span>
        <span className="block text-[11px] font-code text-slate-500 truncate">{pairingLine(ship, pages)}</span>
      </Link>
      <OtpStar ship={ship} charId={charId} />
    </div>
  );
};

export const ShipsPanel = ({ page }) => {
  const { pages } = useWorld();
  const [sort, setSort] = useShipSort();
  const mode = sort === "status" ? "status" : "az";
  const ships = shipsOf(pages, page.id).sort((a, b) => isOtp(b, page.id) - isOtp(a, page.id) || SHIP_SORTERS[mode](a, b));
  if (!ships.length) return null;
  return (
    <div className="rounded-xl border border-pink-500/30 bg-panel p-4" data-testid="page-ships-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg text-pink-200 flex items-center gap-2"><Heart className="w-4 h-4 text-pink-400" /> Ships</h3>
        <button type="button" onClick={() => setSort(mode === "status" ? "az" : "status")} data-testid="ships-panel-sort-toggle"
          className="flex items-center gap-1 text-[10px] font-code uppercase tracking-wider text-slate-400 hover:text-pink-300 border border-line rounded-full px-2 py-0.5 transition-colors">
          <ArrowDownAZ className="w-3 h-3" /> {mode === "status" ? "By status" : "A–Z"}
        </button>
      </div>
      <div className="space-y-1.5">
        {ships.map((s) => <ShipPanelItem key={s.id} ship={s} charId={page.id} />)}
      </div>
    </div>
  );
};
